import { useState } from "react";
import clsx from "clsx";
import { Pill } from "../ui/Pill";
import { ProvenancePopover } from "./ProvenancePopover";
import { t } from "../../i18n/strings";
import type { BooleanWithNotesValue, FieldState, FieldStatus } from "../../engine/types";
import type { FieldSpec, Locale } from "../../templates/types";

const STATUS_TONE: Record<FieldStatus, "neutral" | "sage" | "warn" | "danger"> = {
  unset: "neutral",
  filled: "sage",
  needs_confirmation: "warn",
  unknown: "neutral",
  skipped: "neutral",
};

const STATUS_LABEL_KEY: Record<FieldStatus, "fields.status.unset" | "fields.status.filled" | "fields.status.needsConfirmation" | "fields.status.unknown" | "fields.status.skipped"> = {
  unset: "fields.status.unset",
  filled: "fields.status.filled",
  needs_confirmation: "fields.status.needsConfirmation",
  unknown: "fields.status.unknown",
  skipped: "fields.status.skipped",
};

function formatValue(value: FieldState["value"] | undefined, locale: Locale): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (typeof value[0] === "string") return (value as string[]).join(", ");
    return t(locale, value.length === 1 ? "fields.value.notesRecordedOne" : "fields.value.notesRecordedOther", {
      n: value.length,
    });
  }
  if (typeof value === "object" && "flag" in value) {
    return (value as BooleanWithNotesValue).flag ? t(locale, "common.yes") : t(locale, "common.no");
  }
  return String(value);
}

export function FieldCard({
  field,
  state,
  locale = "en",
}: {
  field: FieldSpec;
  state: FieldState | undefined;
  locale?: Locale;
}) {
  const [open, setOpen] = useState(false);
  const status = state?.status ?? "unset";
  const hasProvenance = Boolean(state?.provenance);

  return (
    <div className="relative rounded-lg border border-border bg-paper p-3">
      <button
        type="button"
        onClick={() => hasProvenance && setOpen((o) => !o)}
        className={clsx("w-full text-left", hasProvenance && "cursor-pointer")}
        aria-haspopup={hasProvenance ? "dialog" : undefined}
        aria-expanded={hasProvenance ? open : undefined}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-ink">{field.label[locale]}</span>
          <Pill tone={STATUS_TONE[status]}>{t(locale, STATUS_LABEL_KEY[status])}</Pill>
        </div>
        {/* §17: "aria-live='polite' on ... field updates" — this is the
            value that actually changes as extractions land; the status pill
            above stays out of the live region so a status-only re-render
            (no value change) doesn't also get announced here. */}
        <p role="status" aria-live="polite" className="mt-1 text-base text-ink">
          {formatValue(state?.value, locale)}
        </p>
        {state?.provenance?.quote && (
          <p className="mt-1 truncate text-xs italic text-ink-soft">"{state.provenance.quote}"</p>
        )}
      </button>
      <ProvenancePopover
        open={open}
        onClose={() => setOpen(false)}
        provenance={state?.provenance ?? null}
        locale={locale}
        fieldLabel={field.label[locale]}
      />
    </div>
  );
}
